import { css } from '@emotion/react';
import {
  Typography,
  LinearProgress,
  Paper,
  TextField,
} from '@mui/material';
import EnhancedTable from '../Table/EnhancedTable';
import { useColumns } from './columns';
import CancelButton from './CancelButton';
import DeleteButton from './DeleteButton';
import StartButton from './StartButton';

import useAutofetchedRunsAsync from 'store/useAutofetchedRunsAsync';

import { toggle, unique } from 'utils/array';

import IRunEntry from 'types/IRunEntry';
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

export default function Runs(props: {
  selectedRunIds: string[];
  setSelectedRunIds: Dispatch<SetStateAction<string[]>>;
}) {
  const { selectedRunIds, setSelectedRunIds } = props;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearch(value);
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        setDebouncedSearch(value);
        setPage(1);
      }, 400);
    },
    [],
  );

  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const runsAsync = useAutofetchedRunsAsync({
    page,
    pageSize,
    search: debouncedSearch || undefined,
  });

  const { status } = runsAsync;
  const paginatedData = runsAsync.status === 'SUCCESS' || runsAsync.status === 'REFRESH'
    ? runsAsync.data
    : undefined;
  const runs = paginatedData?.items ?? [];
  const total = paginatedData?.total ?? 0;

  const columns = useColumns();

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  }, []);

  return (
    <section>
      <Paper elevation={1} style={{ padding: 48 }}>
        <Typography component="h1" variant="h6">
          Workflows
        </Typography>
        <br />
        {status === 'ERROR' && <p>Something went wrong</p>}
        <TextField
          id="search-workflow-runs"
          label="Search"
          variant="outlined"
          fullWidth
          value={search}
          onChange={handleSearchChange}
        />
        <br />
        <br />
        {['INIT', 'PENDING'].includes(status) && <LinearProgress />}
        <EnhancedTable<IRunEntry>
          columns={columns}
          items={runs}
          selection={{
            isSelected: (runId: string) => selectedRunIds.includes(runId),
            selectOnly: (runId: string) => setSelectedRunIds([runId]),
            multi: {
              someSelected: runs.some((run) =>
                selectedRunIds.includes(run.id),
              ),
              allSelected:
                runs.length > 0 &&
                runs.every((run) => selectedRunIds.includes(run.id)),
              toggle: (runId: string) => setSelectedRunIds(toggle(runId)),
              selectAll: () =>
                setSelectedRunIds((ids) =>
                  ids.concat(runs.map((run) => run.id)).filter(unique),
                ),
              deselectAll: () =>
                setSelectedRunIds((ids) =>
                  ids.filter(
                    (id) => !runs.some((run) => run.id === id),
                  ),
                ),
            },
          }}
          serverPagination={{
            page,
            pageSize,
            total,
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
        />
        <br />
        <div
          css={css`
            display: flex;

            > * ~ * {
              margin-left: 16px;
            }
          `}
        >
          <DeleteButton
            selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))}
          />
          <CancelButton
            selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))}
          />
          <StartButton
            selectedRuns={runs.filter((run) => selectedRunIds.includes(run.id))}
          />
        </div>
      </Paper>
    </section>
  );
}
